import PropTypes from "prop-types";
import { Card, Box, Grid, Divider, Chip, IconButton, Tooltip, Typography } from "@mui/material";
import {
  Edit,
  Schedule,
  CalendarMonth,
  Weekend,
  Assessment,
  Toll,
  AccountBalance,
  Percent,
} from "@mui/icons-material";
import MDBox from "shared/components/MDBox";
import MDTypography from "shared/components/MDTypography";
import { estacionesDe, formatPercent as pct, corteDe } from "./configurationModel";
import PeajeChip from "./PeajeChip";

const InfoBlock = ({ icon, label, value, color = "#1a237e" }) => (
  <MDBox
    sx={{
      p: 1.25,
      borderRadius: "10px",
      bgcolor: "#f8fafc",
      border: "1px solid #e2e8f0",
      "& .MuiSvgIcon-root": { color },
      height: "100%",
    }}
  >
    <MDBox display="flex" alignItems="center" gap={0.75} mb={0.5}>
      {icon}
      <MDTypography
        variant="caption"
        sx={{
          fontWeight: 700,
          color: "#475569",
          fontSize: "0.68rem",
          letterSpacing: 0.4,
          textTransform: "uppercase",
        }}
      >
        {label}
      </MDTypography>
    </MDBox>
    <MDTypography
      variant="caption"
      sx={{
        fontWeight: 600,
        color: "#1e293b",
        fontSize: "0.8rem",
        lineHeight: 1.3,
      }}
    >
      {value ?? "—"}
    </MDTypography>
  </MDBox>
);

InfoBlock.propTypes = {
  icon: PropTypes.node.isRequired,
  label: PropTypes.string.isRequired,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  color: PropTypes.string,
};

InfoBlock.defaultProps = { value: "", color: "#1a237e" };

const ConcesionCard = ({ concesion, onEdit }) => {
  const { id, name } = concesion;
  const rules = concesion.rules || {};
  const politica = rules.politica_pago || {};
  const estaciones = estacionesDe(concesion);

  const nombreMostrar = rules.nombre || name;

  const corteTexto = corteDe(estaciones);

  const esConsolidado = politica.modalidad === "CONSOLIDADO";

  return (
    <Card
      sx={{
        borderRadius: "14px",
        border: "1px solid",
        borderColor: "divider",
        boxShadow: "0 2px 10px rgba(15, 23, 42, 0.04)",
        overflow: "hidden",
        height: "100%",
        display: "flex",
        flexDirection: "column",
        transition: "all 0.2s ease",
        "&:hover": {
          boxShadow: "0 6px 20px rgba(15, 23, 42, 0.10)",
          transform: "translateY(-2px)",
        },
      }}
    >
      {/* HEADER */}
      <Box
        sx={{
          px: 2,
          py: 1.5,
          bgcolor: "#edf3f8",
          color: "#243b53",
          borderBottom: "1px solid #dce5ed",
        }}
      >
        <MDBox display="flex" alignItems="center" justifyContent="space-between" gap={1.5}>
          <MDBox display="flex" alignItems="center" gap={1}>
            <Box
              sx={{
                bgcolor: "#dde9f2",
                borderRadius: "8px",
                p: 0.6,
                display: "flex",
              }}
            >
              <Toll sx={{ fontSize: 18, color: "#35617d" }} />
            </Box>
            <MDBox>
              <Typography
                variant="subtitle1"
                fontWeight={700}
                sx={{ color: "#243b53", lineHeight: 1.35, overflowWrap: "anywhere" }}
              >
                {nombreMostrar}
              </Typography>
              <Typography
                variant="caption"
                sx={{ color: "#526579", fontSize: "0.75rem", display: "block", mt: 0.5 }}
              >
                Código {concesion.code ?? "—"} · {estaciones.length}{" "}
                {estaciones.length === 1 ? "peaje" : "peajes"}
              </Typography>
            </MDBox>
          </MDBox>

          <Tooltip title="Editar configuración">
            <IconButton
              size="small"
              aria-label={`Editar ${nombreMostrar}`}
              onClick={() => onEdit?.(concesion)}
              sx={{
                color: "#35617d",
                bgcolor: "#fff",
                border: "1px solid #cfdae4",
                flexShrink: 0,
                "&:hover": { bgcolor: "#dde9f2" },
              }}
            >
              <Edit sx={{ fontSize: 16 }} />
            </IconButton>
          </Tooltip>
        </MDBox>
      </Box>

      {/* CUERPO */}
      <Box sx={{ p: 1.75, flexGrow: 1 }}>
        {/* Tipo consolidado */}
        <MDBox mb={1.5} display="flex" flexWrap="wrap" gap={0.5}>
          <Chip
            size="small"
            icon={<Assessment sx={{ fontSize: 14 }} />}
            label={politica.descripcion_modalidad || "Sin modalidad"}
            sx={{
              bgcolor: esConsolidado ? "#e8f5e9" : "#fff3e0",
              color: esConsolidado ? "#2e7d32" : "#e65100",
              fontWeight: 700,
              fontSize: "0.75rem",
              height: "auto",
              "& .MuiChip-label": { whiteSpace: "normal", py: 0.75 },
              border: `1px solid ${esConsolidado ? "#a5d6a7" : "#ffcc80"}`,
              "& .MuiChip-icon": {
                color: esConsolidado ? "#2e7d32" : "#e65100",
              },
            }}
          />
          <Chip
            size="small"
            icon={<Schedule sx={{ fontSize: 14 }} />}
            label={rules.tipo_corte || "sin corte"}
            sx={{
              bgcolor: "#f1f5f9",
              color: "#475569",
              fontWeight: 700,
              fontSize: "0.68rem",
              border: "1px solid #d5dfe8",
              "& .MuiChip-icon": { color: "#64748b" },
            }}
          />
        </MDBox>

        {/* INFO PRINCIPAL */}
        <Grid container spacing={1}>
          <Grid item xs={12} sm={6}>
            <InfoBlock
              icon={<Schedule sx={{ fontSize: 14, color: "#3f51b5" }} />}
              label="Corte horario"
              value={corteTexto}
              color="#3f51b5"
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <InfoBlock
              icon={<CalendarMonth sx={{ fontSize: 14, color: "#2e7d32" }} />}
              label="Liquidación L-V"
              value={`${politica.frecuencia_lv || "—"} · ${politica.dias_traslado_lv || "—"}`}
              color="#2e7d32"
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <InfoBlock
              icon={<Weekend sx={{ fontSize: 14, color: "#ad1457" }} />}
              label="Liquidación FDS"
              value={`${politica.frecuencia_fds || "—"} · ${politica.dias_traslado_fds || "—"}`}
              color="#ad1457"
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <InfoBlock
              icon={<Percent sx={{ fontSize: 14, color: "#6a1b9a" }} />}
              label="Comisión"
              value={pct(rules.pct_comision)}
              color="#6a1b9a"
            />
          </Grid>
        </Grid>

        {/* PARÁMETROS FINANCIEROS */}
        <Divider sx={{ my: 1.5 }} />
        <MDTypography
          variant="caption"
          sx={{
            fontWeight: 700,
            color: "#64748b",
            fontSize: "0.68rem",
            letterSpacing: 0.5,
            textTransform: "uppercase",
          }}
        >
          Parámetros financieros
        </MDTypography>

        <Grid container spacing={1} mt={0.25}>
          <Grid item xs={6}>
            <InfoBlock
              icon={<AccountBalance sx={{ fontSize: 14, color: "#00838f" }} />}
              label="Retefuente"
              value={pct(rules.pct_retefuente)}
              color="#00838f"
            />
          </Grid>
          <Grid item xs={6}>
            <InfoBlock
              icon={<AccountBalance sx={{ fontSize: 14, color: "#00838f" }} />}
              label="ReteIVA"
              value={pct(rules.pct_reteiva)}
              color="#00838f"
            />
          </Grid>
          <Grid item xs={6}>
            <InfoBlock
              icon={<AccountBalance sx={{ fontSize: 14, color: "#4527a0" }} />}
              label="ReteICA"
              value={
                rules.tipo_reteica === "fijo"
                  ? rules.reteica_fijo
                  : `por comercio (${Object.keys(rules.reteica_map || {}).length})`
              }
              color="#4527a0"
            />
          </Grid>
          <Grid item xs={6}>
            <InfoBlock
              icon={<Percent sx={{ fontSize: 14, color: "#4527a0" }} />}
              label="IVA Comisión"
              value={pct(rules.pct_iva_comision)}
              color="#4527a0"
            />
          </Grid>
        </Grid>

        <Divider sx={{ my: 1.5 }} />

        {/* PEAJES */}
        <MDTypography
          variant="caption"
          sx={{
            fontWeight: 700,
            color: "#64748b",
            fontSize: "0.68rem",
            letterSpacing: 0.5,
            textTransform: "uppercase",
          }}
        >
          Peajes
        </MDTypography>
        <MDBox mt={0.75} display="flex" flexWrap="wrap" gap={0.25}>
          {estaciones.length === 0 ? (
            <MDTypography variant="caption" color="text.secondary">
              Sin peajes asociados
            </MDTypography>
          ) : (
            estaciones.map((p, idx) => <PeajeChip key={`${id}-${idx}`} nombre={p.peaje} />)
          )}
        </MDBox>
      </Box>
    </Card>
  );
};

ConcesionCard.propTypes = {
  concesion: PropTypes.object.isRequired,
  onEdit: PropTypes.func,
};

ConcesionCard.defaultProps = { onEdit: null };

export default ConcesionCard;
